import { useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetPatientQuery } from '@/core/services/patient'

type SelectionState = {
  primaryPatientId: string | null
  secondaryPatientId: string | null
}

export function usePatientSelection() {
  const [{ primaryPatientId, secondaryPatientId }, setSelectionState] = useState<SelectionState>({ primaryPatientId: null, secondaryPatientId: null })

  const { data: primaryPatient } = useGetPatientQuery(primaryPatientId ?? skipToken, { refetchOnMountOrArgChange: true })
  const { data: secondaryPatient } = useGetPatientQuery(secondaryPatientId ?? skipToken, { refetchOnMountOrArgChange: true })

  const select = (patientId: string) => {
    setSelectionState((prev) => {
      if (!prev.primaryPatientId) return { ...prev, primaryPatientId: patientId }
      if (prev.primaryPatientId === patientId) return { primaryPatientId: prev.secondaryPatientId, secondaryPatientId: null }
      if (prev.secondaryPatientId === patientId) return { ...prev, secondaryPatientId: null }
      return { ...prev, secondaryPatientId: patientId }
    })
  }

  const unselect = (patientId: string) => {
    setSelectionState((prev) => {
      if (prev.primaryPatientId === patientId) return { primaryPatientId: prev.secondaryPatientId, secondaryPatientId: null }
      if (prev.secondaryPatientId === patientId) return { ...prev, secondaryPatientId: null }
      return prev
    })
  }

  const swap = () => {
    setSelectionState((prev) => {
      if (!prev.primaryPatientId || !prev.secondaryPatientId) return prev
      return { primaryPatientId: prev.secondaryPatientId, secondaryPatientId: prev.primaryPatientId }
    })
  }

  const reset = () => {
    setSelectionState({ primaryPatientId: null, secondaryPatientId: null })
  }

  const activeStep = !primaryPatientId ? 0 : !secondaryPatientId ? 1 : 2

  return {
    primaryPatient: primaryPatientId ? primaryPatient : undefined,
    secondaryPatient: secondaryPatientId ? secondaryPatient : undefined,
    activeStep,
    select,
    unselect,
    swap,
    reset,
  }
}
